import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import PhotoGallery from '@/components/PhotoGallery';
import { JobCompletedPrompt } from '@/components/WhatsNextPrompt';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  MapPin,
  User,
  Clock,
  FileText,
  Receipt,
  Users,
  Briefcase,
  MoreHorizontal,
  Download,
  Edit,
  Trash2,
  ExternalLink,
  CheckCircle,
  Pause,
  XCircle,
  Play,
  List,
  Grid3X3,
  Phone,
  Navigation,
  MoreVertical,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  Repeat,
  StopCircle
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, parseISO, isToday, isBefore, isAfter } from 'date-fns';

// Job statuses with translation keys
const JOB_STATUSES = [
  { value: 'scheduled', label: 'Scheduled', labelKey: 'scheduled', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CalendarIcon },
  { value: 'in_progress', label: 'In Progress', labelKey: 'in_progress', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Play },
  { value: 'completed', label: 'Completed', labelKey: 'completed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  { value: 'on_hold', label: 'On Hold', labelKey: 'on_hold', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Pause },
  { value: 'cancelled', label: 'Cancelled', labelKey: 'cancelled', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
];

const DUTY_LABELS = {
  estimator: 'Estimator',
  project_manager: 'Project Manager',
  crew_lead: 'Crew Lead',
  crew: 'Crew',
  other: 'Other'
};

const JOB_COLORS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#64748b', label: 'Gray' }
];

// Swipeable Job Card Component
const SwipeableJobCard = ({ job, statusConfig, onTap, onStatusChange, onCall, onDirections }) => {
  const { t } = useLanguage();
  const cardRef = useRef(null);
  const [swipeX, setSwipeX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontalSwipe = useRef(false);

  const SWIPE_THRESHOLD = 80;
  const ACTION_WIDTH = 160;

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = false;
    setIsDragging(true);
    
    // Long press detection
    const timer = setTimeout(() => {
      setShowActions(true);
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - startX.current;
    const deltaY = currentY - startY.current;
    
    // Determine if this is a horizontal swipe
    if (!isHorizontalSwipe.current && Math.abs(deltaX) > 10) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }
    
    // Cancel long press if moving
    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    }
    
    if (isHorizontalSwipe.current) {
      e.preventDefault();
      // Only allow left swipe (negative) to reveal actions
      const newX = Math.max(-ACTION_WIDTH, Math.min(0, deltaX));
      setSwipeX(newX);
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    setIsDragging(false);
    
    // Snap to position
    if (swipeX < -SWIPE_THRESHOLD) {
      setSwipeX(-ACTION_WIDTH);
    } else {
      setSwipeX(0);
    }
  };

  const handleClick = (e) => {
    if (swipeX !== 0 || showActions) {
      setSwipeX(0);
      setShowActions(false);
      return;
    }
    onTap();
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    setShowActions(false);
    setSwipeX(0);
    // Small delay to let modal close, then open detail
    setTimeout(() => {
      onTap();
    }, 100);
  };

  const handleMarkComplete = (e) => {
    e.stopPropagation();
    onStatusChange('completed');
    setSwipeX(0);
    setShowActions(false);
  };

  const handleMarkInProgress = (e) => {
    e.stopPropagation();
    onStatusChange('in_progress');
    setSwipeX(0);
    setShowActions(false);
  };

  const handleCallClient = (e) => {
    e.stopPropagation();
    onCall();
    setShowActions(false);
  };

  const handleGetDirections = (e) => {
    e.stopPropagation();
    onDirections();
    setShowActions(false);
  };

  return (
    <div className="relative overflow-hidden rounded-xl" data-testid={`job-list-item-${job.id}`}>
      {/* Swipe Actions (behind the card) */}
      <div className="absolute inset-y-0 right-0 flex items-stretch">
        {job.status !== 'completed' && (
          <button
            onClick={handleMarkComplete}
            className="w-20 bg-green-500 text-white flex flex-col items-center justify-center"
          >
            <CheckCircle className="w-6 h-6 mb-1" />
            <span className="text-xs font-medium">{t('done')}</span>
          </button>
        )}
        {job.status === 'scheduled' && (
          <button
            onClick={handleMarkInProgress}
            className="w-20 bg-amber-500 text-white flex flex-col items-center justify-center"
          >
            <Play className="w-6 h-6 mb-1" />
            <span className="text-xs font-medium">{t('start')}</span>
          </button>
        )}
      </div>

      {/* Main Card Content */}
      <div
        ref={cardRef}
        className="relative bg-white border shadow-sm transition-transform touch-pan-y"
        style={{ transform: `translateX(${swipeX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Color indicator */}
            <div 
              className="w-1.5 h-full min-h-[60px] rounded-full flex-shrink-0"
              style={{ backgroundColor: job.color || '#3b82f6' }}
            />
            
            <div className="flex-1 min-w-0">
              {/* Title and Status */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold text-gray-900 leading-tight">
                  {job.title}
                </h3>
                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              </div>
              
              {/* Client */}
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{job.client?.name || 'Unknown Client'}</span>
                {job.client?.phone && (
                  <button
                    onClick={handleCallClient}
                    className="text-primary p-1 -m-1 rounded-full hover:bg-primary/10"
                  >
                    <Phone className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Address */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate flex-1">{job.address || 'No address'}</span>
                {job.address && (
                  <button
                    onClick={handleGetDirections}
                    className="text-primary p-1 -m-1 rounded-full hover:bg-primary/10"
                  >
                    <Navigation className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Job Type */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                <span>{job.job_type}</span>
              </div>
            </div>
            
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </div>
          
          {/* Swipe hint */}
          {swipeX === 0 && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-300 flex items-center gap-1 pointer-events-none">
              <ArrowRight className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>

      {/* Long Press Quick Actions Modal */}
      {showActions && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center p-4"
          onClick={() => setShowActions(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl animate-in slide-in-from-bottom duration-200"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b">
              <h3 className="font-semibold text-lg">{job.title}</h3>
              <p className="text-sm text-gray-500">{job.client?.name}</p>
            </div>
            
            <div className="p-2">
              {job.status !== 'completed' && (
                <button
                  onClick={handleMarkComplete}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-green-50 text-green-700"
                >
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-medium">Mark as Completed</span>
                </button>
              )}
              
              {job.status === 'scheduled' && (
                <button
                  onClick={handleMarkInProgress}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-amber-50 text-amber-700"
                >
                  <Play className="w-6 h-6" />
                  <span className="font-medium">Start Job</span>
                </button>
              )}
              
              {job.client?.phone && (
                <button
                  onClick={handleCallClient}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 text-blue-700"
                >
                  <Phone className="w-6 h-6" />
                  <span className="font-medium">Call {job.client.name}</span>
                </button>
              )}
              
              {job.address && (
                <button
                  onClick={handleGetDirections}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-purple-50 text-purple-700"
                >
                  <Navigation className="w-6 h-6" />
                  <span className="font-medium">Get Directions</span>
                </button>
              )}
              
              <button
                onClick={handleViewDetails}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-gray-700"
              >
                <FileText className="w-6 h-6" />
                <span className="font-medium">View Details</span>
              </button>
            </div>
            
            <div className="p-2 border-t">
              <button
                onClick={() => setShowActions(false)}
                className="w-full p-3 text-center text-gray-500 font-medium rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const JobsPage = () => {
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightJobId = searchParams.get('highlight');
  const actionParam = searchParams.get('action');
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Main tab state: calendar, active, appointments
  const [activeTab, setActiveTab] = useState('calendar');
  
  // Handler for tab changes that scrolls to top
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Calendar state - default to list on mobile
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(() => {
    // Default to list view on mobile
    return window.innerWidth < 768 ? 'list' : 'month';
  });
  
  // Active jobs filter state
  const [activeJobsFilter, setActiveJobsFilter] = useState('all');
  
  // Site Visits state
  const [siteVisits, setSiteVisits] = useState([]);
  
  // Modal/Sheet state
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  
  // Job Completed prompt state
  const [showJobCompletedPrompt, setShowJobCompletedPrompt] = useState(false);
  const [completedJobForPrompt, setCompletedJobForPrompt] = useState(null);
  
  // Check for action=new to auto-open modal
  useEffect(() => {
    if (actionParam === 'new') {
      setShowNewJobModal(true);
      // Clear the action param
      setSearchParams({});
    }
  }, [actionParam, setSearchParams]);
  
  // Staff assignment state
  const [employees, setEmployees] = useState([]);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [selectedStaffEmployee, setSelectedStaffEmployee] = useState('');
  const [selectedStaffDuty, setSelectedStaffDuty] = useState('crew');
  
  // Form state - add job_entry_type
  const [jobForm, setJobForm] = useState({
    client_id: '',
    title: '',
    job_type: '',
    job_entry_type: 'job', // 'job' or 'estimate_appointment'
    address: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    start_time: '08:00',
    end_time: '17:00',
    all_day: true,
    assigned_crew: [],
    internal_notes: '',
    color: '#3b82f6',
    // Recurring job settings
    is_recurring: false,
    recurring_frequency: 'weekly',
    recurring_interval_days: null,
    recurring_end_date: '',
    recurring_occurrences: null
  });

  useEffect(() => {
    if (organization?.id) {
      loadData();
    }
  }, [organization?.id]);
  
  // Handle deep link to specific job
  useEffect(() => {
    if (highlightJobId && jobs.length > 0) {
      const jobToHighlight = jobs.find(j => j.id === highlightJobId);
      if (jobToHighlight) {
        // Enrich with client data
        const enrichedJob = {
          ...jobToHighlight,
          client: clients.find(c => c.id === jobToHighlight.client_id)
        };
        setSelectedJob(enrichedJob);
        setShowJobDetail(true);
        // Clear the highlight param from URL
        setSearchParams({});
      }
    }
  }, [highlightJobId, jobs, clients]);

  const loadData = async () => {
    try {
      const [jobsRes, clientsRes, usersRes, employeesRes, siteVisitsRes] = await Promise.all([
        apiHelpers.getJobs(organization.id),
        apiHelpers.getClients(organization.id),
        apiHelpers.getUsers(organization.id),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees?active_only=true`).then(r => r.json()),
        apiHelpers.getSiteVisits(organization.id)
      ]);
      setJobs(jobsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setEmployees(employeesRes || []);
      setSiteVisits(siteVisitsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  };

  // Calendar calculations
  const calendarDays = useMemo(() => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 });
      const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      return eachDayOfInterval({ start, end });
    }
  }, [currentDate, viewMode]);

  // Get calendar entries for a specific day (jobs + site visits)
  const getCalendarEntriesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Get jobs that span this date (only if they have a start_date)
    const dayJobs = jobs.filter(job => {
      if (!job.start_date) return false;
      const startDate = job.start_date.split('T')[0]; // Handle ISO datetime strings
      const endDate = job.end_date ? job.end_date.split('T')[0] : startDate;
      return dateStr >= startDate && dateStr <= endDate;
    }).map(job => ({
      ...job,
      entryType: 'job',
      displayColor: getJobColor(job.status)
    }));
    
    // Get site visits scheduled for this date (only scheduled ones)
    const dayVisits = siteVisits.filter(visit => 
      visit.scheduled_date === dateStr && visit.status === 'scheduled'
    ).map(visit => ({
      ...visit,
      entryType: 'site_visit',
      displayColor: '#8b5cf6', // Purple for site visits
      title: `📍 ${visit.client_name || 'Site Visit'}`,
      start_time: visit.scheduled_time
    }));
    
    return [...dayVisits, ...dayJobs];
  };
  
  // Get color based on job status
  const getJobColor = (status) => {
    switch (status) {
      case 'scheduled': return '#3b82f6'; // Blue
      case 'in_progress': return '#f59e0b'; // Amber
      case 'completed': return '#22c55e'; // Green
      case 'cancelled': return '#ef4444'; // Red
      default: return '#6b7280'; // Gray
    }
  };

  // Legacy function for backwards compatibility
  const getJobsForDay = (date) => {
    return getCalendarEntriesForDay(date);
  };

  const navigateCalendar = (direction) => {
    if (viewMode === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCreateJob = async () => {
    if (!jobForm.client_id || !jobForm.title || !jobForm.start_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await apiHelpers.createJob(organization.id, jobForm, user.id, user.name);
      setJobs([...jobs, response.data]);
      setShowNewJobModal(false);
      resetForm();
      toast.success('Job scheduled!');
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job');
    }
  };

  const handleUpdateJob = async (jobId, updates) => {
    try {
      const response = await apiHelpers.updateJob(organization.id, jobId, updates, user.id, user.name);
      setJobs(jobs.map(j => j.id === jobId ? response.data : j));
      if (selectedJob?.id === jobId) {
        setSelectedJob(response.data);
      }
      toast.success('Job updated!');
      
      // Show "What's Next" prompt if job was marked as completed
      if (updates.status === 'completed') {
        setCompletedJobForPrompt(response.data);
        setShowJobCompletedPrompt(true);
      }
    } catch (error) {
      console.error('Failed to update job:', error);
      toast.error('Failed to update job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    const confirmed = await showConfirm({
      title: 'Delete Job?',
      description: 'This action cannot be undone. The job will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    
    if (!confirmed) return;

    try {
      await apiHelpers.deleteJob(organization.id, jobId, user.id);
      setJobs(jobs.filter(j => j.id !== jobId));
      setShowJobDetail(false);
      setSelectedJob(null);
      toast.success('Job deleted');
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleExportIcal = async (jobId) => {
    try {
      const response = await apiHelpers.getJobIcal(organization.id, jobId);
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job-${jobId.slice(0, 8)}.ics`;
      a.click();
      toast.success('Calendar file downloaded');
    } catch (error) {
      console.error('Failed to export iCal:', error);
      toast.error('Failed to export');
    }
  };

  // Add to Google Calendar - opens Google Calendar with pre-filled event
  const handleAddToGoogleCalendar = (job) => {
    if (!job) return;
    
    // Format dates for Google Calendar URL (YYYYMMDDTHHMMSS format)
    const formatDateForGoogle = (dateStr, timeStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':');
        date.setHours(parseInt(hours), parseInt(minutes), 0);
      }
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    // Build event details
    const title = encodeURIComponent(job.title || `Job at ${job.address || 'TBD'}`);
    const clientName = job.client_name || '';
    const description = encodeURIComponent([
      job.type ? `Type: ${job.type}` : '',
      clientName ? `Client: ${clientName}` : '',
      job.phone ? `Phone: ${job.phone}` : '',
      job.internal_notes ? `\nNotes: ${job.internal_notes}` : ''
    ].filter(Boolean).join('\n'));
    
    const location = encodeURIComponent(job.address || '');
    
    // Calculate start and end times
    let startDate = job.start_date || new Date().toISOString().split('T')[0];
    let endDate = job.end_date || startDate;
    let startTime = job.start_time || '09:00';
    let endTime = job.end_time || '17:00';
    
    const dates = `${formatDateForGoogle(startDate, startTime)}/${formatDateForGoogle(endDate, endTime)}`;
    
    // Build Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${description}&location=${location}&sf=true&output=xml`;
    
    // Open in new tab
    window.open(googleCalendarUrl, '_blank');
    toast.success('Opening Google Calendar...');
  };

  const handleDownloadPdf = async (jobId) => {
    try {
      const response = await apiHelpers.getJobPdf(organization.id, jobId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `job-${jobId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Job sheet PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Staff assignment functions
  const handleAddStaffToJob = async () => {
    if (!selectedStaffEmployee || !selectedJob) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${selectedJob.id}/staff`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employee_id: selectedStaffEmployee,
            duty: selectedStaffDuty
          })
        }
      );
      
      if (response.ok) {
        const updatedJob = await response.json();
        setJobs(jobs.map(j => j.id === selectedJob.id ? updatedJob : j));
        setSelectedJob(updatedJob);
        setShowAddStaff(false);
        setSelectedStaffEmployee('');
        setSelectedStaffDuty('crew');
        toast.success('Staff assigned to job!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign staff');
      }
    } catch (error) {
      console.error('Failed to add staff:', error);
      toast.error('Failed to assign staff');
    }
  };

  const handleRemoveStaffFromJob = async (employeeId) => {
    if (!selectedJob) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${selectedJob.id}/staff/${employeeId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        const updatedJob = await response.json();
        setJobs(jobs.map(j => j.id === selectedJob.id ? updatedJob : j));
        setSelectedJob(updatedJob);
        toast.success('Staff removed from job');
      }
    } catch (error) {
      console.error('Failed to remove staff:', error);
      toast.error('Failed to remove staff');
    }
  };

  const resetForm = () => {
    setJobForm({
      client_id: '',
      title: '',
      job_type: '',
      job_entry_type: 'job',
      address: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      start_time: '08:00',
      end_time: '17:00',
      all_day: true,
      assigned_crew: [],
      internal_notes: '',
      color: '#3b82f6'
    });
  };

  const openJobDetail = (job) => {
    navigate(`/jobs/${job.id}`);
  };

  const getStatusConfig = (status) => {
    return JOB_STATUSES.find(s => s.value === status) || JOB_STATUSES[0];
  };

  // Filter jobs based on type
  const actualJobs = useMemo(() => 
    jobs.filter(job => !job.job_entry_type || job.job_entry_type === 'job'),
    [jobs]
  );

  const estimateAppointments = useMemo(() => 
    jobs.filter(job => job.job_entry_type === 'estimate_appointment'),
    [jobs]
  );

  // Filter active jobs by status
  const filteredActiveJobs = useMemo(() => {
    let filtered = actualJobs; // Don't filter by start_date - show all jobs
    if (activeJobsFilter !== 'all') {
      filtered = filtered.filter(job => job.status === activeJobsFilter);
    }
    // Sort by start_date descending (jobs without dates go to the end)
    return filtered.sort((a, b) => {
      if (!a.start_date && !b.start_date) return 0;
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      return new Date(b.start_date) - new Date(a.start_date);
    });
  }, [actualJobs, activeJobsFilter]);

  // Handle client selection to auto-fill address
  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setJobForm(prev => ({
      ...prev,
      client_id: clientId,
      address: client?.address || prev.address
    }));
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t('jobs_title')}</h1>
          <p className="text-muted-foreground text-sm sm:text-base">{t('schedule_manage')}</p>
        </div>
        
        {/* Primary action: New Site Visit (follows workflow: Site Visit → Estimate → Job) */}
        <Button 
          onClick={() => navigate('/site-visits?action=new')}
          className="btn-primary"
          data-testid="new-site-visit-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Site Visit
        </Button>
      </div>

      {/* Main Tab Navigation */}
      <div className="bg-gray-100 p-1 rounded-xl mb-4 sm:mb-6">
        <div className="grid grid-cols-3 gap-1">
          <button
            onClick={() => handleTabChange('calendar')}
            className={`px-3 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'calendar' 
                ? 'bg-white shadow-sm text-foreground' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-calendar"
          >
            <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mx-auto sm:hidden mb-1" />
            <span className="hidden sm:inline">{t('calendar')}</span>
            <span className="sm:hidden text-xs">{t('calendar')}</span>
          </button>
          <button
            onClick={() => handleTabChange('active')}
            className={`px-3 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'active' 
                ? 'bg-white shadow-sm text-foreground' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-active-jobs"
          >
            <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 mx-auto sm:hidden mb-1" />
            <span className="hidden sm:inline">{t('active_jobs')}</span>
            <span className="sm:hidden text-xs">{t('active')}</span>
            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
              {actualJobs.filter(j => j.status !== 'completed' && j.status !== 'cancelled').length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('appointments')}
            className={`px-3 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'appointments' 
                ? 'bg-white shadow-sm text-foreground' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-site-visits"
          >
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mx-auto sm:hidden mb-1" />
            <span className="hidden sm:inline">{t('site_visits_nav')}</span>
            <span className="sm:hidden text-xs">{t('visits')}</span>
            <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
              {siteVisits.filter(v => v.status === 'scheduled').length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('recurring')}
            className={`px-3 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all ${
              activeTab === 'recurring' 
                ? 'bg-white shadow-sm text-foreground' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-recurring"
          >
            <Repeat className="w-4 h-4 sm:w-5 sm:h-5 mx-auto sm:hidden mb-1" />
            <span className="hidden sm:inline">Recurring</span>
            <span className="sm:hidden text-xs">Recur.</span>
            <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
              {jobs.filter(j => j.is_recurring && !j.recurring_parent_id).length}
            </span>
          </button>
        </div>
      </div>

      {/* Calendar Tab Content */}
      {activeTab === 'calendar' && (
        <>
          {/* Calendar Controls */}
          <div className="card-default mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateCalendar('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigateCalendar('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>{t('today')}</Button>
                <h2 className="font-heading text-base sm:text-xl font-semibold ml-1 sm:ml-2">
                  {viewMode === 'month' 
                    ? format(currentDate, 'MMM yyyy')
                    : viewMode === 'week'
                    ? `${t('week')} ${format(startOfWeek(currentDate), 'MMM d')}`
                    : format(currentDate, 'MMMM yyyy')
                  }
                </h2>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="flex-1 sm:flex-none"
                >
                  <List className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('list')}</span>
                </Button>
                <Button
                  variant={viewMode === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('week')}
                  className="flex-1 sm:flex-none"
                >
                  <Grid3X3 className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('week')}</span>
                </Button>
                <Button
                  variant={viewMode === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('month')}
                  className="flex-1 sm:flex-none"
                >
                  <CalendarIcon className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('month')}</span>
                </Button>
          </div>
        </div>
      </div>
      
      {/* Calendar Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs sm:text-sm">
        <span className="text-muted-foreground font-medium">Legend:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8b5cf6' }}></div>
          <span>{t('site_visits_nav')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3b82f6' }}></div>
          <span>{t('scheduled')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
          <span>{t('in_progress')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }}></div>
          <span>{t('completed')}</span>
        </div>
      </div>

      {/* List View (Mobile-Friendly) */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {/* Upcoming Jobs */}
          {jobs.length === 0 ? (
            <div className="card-default text-center py-12">
              <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-muted-foreground">{t('no_jobs_scheduled_yet')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('start_by_scheduling')}</p>
              <Button onClick={() => navigate('/site-visits?action=new')} className="mt-4 btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                {t('schedule_site_visit')}
              </Button>
            </div>
          ) : (
            <>
              {/* Unscheduled jobs banner — show jobs with no start_date that need attention */}
              {jobs.filter(j => !j.start_date && j.status !== 'cancelled' && j.status !== 'completed').length > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 rounded-lg mb-2 bg-amber-100 text-amber-800 flex items-center justify-between">
                    <span className="font-semibold text-sm flex items-center gap-2">
                      ⚠️ Needs Scheduling
                      <span className="text-xs opacity-75">({jobs.filter(j => !j.start_date && j.status !== 'cancelled' && j.status !== 'completed').length})</span>
                    </span>
                  </div>
                  <div className="space-y-2">
                    {jobs
                      .filter(j => !j.start_date && j.status !== 'cancelled' && j.status !== 'completed')
                      .map(job => {
                        const statusConfig = getStatusConfig(job.status);
                        return (
                          <SwipeableJobCard
                            key={job.id}
                            job={job}
                            statusConfig={statusConfig}
                            onTap={() => openJobDetail(job)}
                            onStatusChange={(status) => handleUpdateJob(job.id, { status })}
                            onCall={() => {
                              if (job.client?.phone) {
                                window.location.href = `tel:${job.client.phone}`;
                              }
                            }}
                            onDirections={() => {
                              if (job.address) {
                                window.open(`https://maps.google.com?q=${encodeURIComponent(job.address)}`, '_blank');
                              }
                            }}
                          />
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Group jobs by date */}
              {Object.entries(
                jobs
                  .filter(job => {
                    if (!job.start_date) return false;
                    const jobDate = parseISO(job.start_date);
                    const monthStart = startOfMonth(currentDate);
                    const monthEnd = endOfMonth(currentDate);
                    return jobDate >= monthStart && jobDate <= monthEnd;
                  })
                  .sort((a, b) => a.start_date.localeCompare(b.start_date))
                  .reduce((groups, job) => {
                    const date = job.start_date;
                    if (!groups[date]) groups[date] = [];
                    groups[date].push(job);
                    return groups;
                  }, {})
              ).map(([date, dayJobs]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className={`sticky top-0 z-10 px-3 py-2 rounded-lg mb-2 ${
                    isToday(parseISO(date)) 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    <span className="font-semibold">
                      {isToday(parseISO(date)) 
                        ? 'Today' 
                        : format(parseISO(date), 'EEEE, MMMM d')
                      }
                    </span>
                  </div>
                  
                  {/* Jobs for this date */}
                  <div className="space-y-2">
                    {dayJobs.map(job => {
                      const statusConfig = getStatusConfig(job.status);
                      return (
                        <SwipeableJobCard
                          key={job.id}
                          job={job}
                          statusConfig={statusConfig}
                          onTap={() => openJobDetail(job)}
                          onStatusChange={(status) => handleUpdateJob(job.id, { status })}
                          onCall={() => {
                            if (job.client?.phone) {
                              window.location.href = `tel:${job.client.phone}`;
                            }
                          }}
                          onDirections={() => {
                            if (job.address) {
                              window.open(`https://maps.google.com?q=${encodeURIComponent(job.address)}`, '_blank');
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* No jobs this month message */}
              {jobs.filter(job => {
                if (!job.start_date) return false;
                const jobDate = parseISO(job.start_date);
                const monthStart = startOfMonth(currentDate);
                const monthEnd = endOfMonth(currentDate);
                return jobDate >= monthStart && jobDate <= monthEnd;
              }).length === 0 && jobs.filter(j => !j.start_date && j.status !== 'cancelled' && j.status !== 'completed').length === 0 && (
                <div className="card-default text-center py-8">
                  <p className="text-muted-foreground">No jobs scheduled for {format(currentDate, 'MMMM yyyy')}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Calendar Grid (Desktop) */}
      {(viewMode === 'month' || viewMode === 'week') && (
        <div className="card-default p-0 overflow-hidden hidden md:block">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-muted-foreground border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className={`grid grid-cols-7 ${viewMode === 'week' ? 'min-h-[400px]' : ''}`}>
            {calendarDays.map((day, index) => {
              const dayJobs = getJobsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isCurrentDay = isToday(day);
              
              return (
                <div
                  key={index}
                  className={`min-h-[120px] border-r border-b last:border-r-0 p-2 ${
                    !isCurrentMonth ? 'bg-gray-50' : ''
                  } ${isCurrentDay ? 'bg-blue-50' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    !isCurrentMonth ? 'text-gray-400' : ''
                  } ${isCurrentDay ? 'text-blue-600' : ''}`}>
                    {format(day, 'd')}
                  </div>
                  
                  <div className="space-y-1">
                    {dayJobs.slice(0, 3).map(entry => (
                      <button
                        key={entry.id}
                        onClick={() => {
                          if (entry.entryType === 'site_visit') {
                            navigate(`/site-visits/${entry.id}`);
                          } else {
                            openJobDetail(entry);
                          }
                        }}
                        className="w-full text-left px-2 py-1 rounded text-xs font-medium truncate transition-all hover:scale-105"
                        style={{ 
                          backgroundColor: `${entry.displayColor || entry.color || '#3b82f6'}20`,
                          color: entry.displayColor || entry.color || '#3b82f6',
                          borderLeft: `3px solid ${entry.displayColor || entry.color || '#3b82f6'}`
                        }}
                        data-testid={`calendar-entry-${entry.id}`}
                      >
                        {entry.entryType === 'site_visit' ? '📍 ' : ''}{entry.title}
                      </button>
                    ))}
                    {dayJobs.length > 3 && (
                      <p className="text-xs text-muted-foreground px-2">
                        +{dayJobs.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Calendar (Simplified) */}
      {(viewMode === 'month' || viewMode === 'week') && (
        <div className="md:hidden">
          <div className="card-default text-center py-6">
            <p className="text-muted-foreground mb-4">{t('calendar_best_on_larger')}</p>
            <Button onClick={() => setViewMode('list')} variant="outline">
              <List className="w-4 h-4 mr-2" />
              Switch to List View
            </Button>
          </div>
        </div>
      )}

      {/* Stats Bar - Show only in Calendar tab — tappable to jump to filtered Active list */}
      {activeTab === 'calendar' && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-4 mt-4 md:mt-6">
          {JOB_STATUSES.slice(0, 3).map(status => {
            const count = jobs.filter(j => j.status === status.value).length;
            return (
              <button
                key={status.value}
                onClick={() => {
                  setActiveJobsFilter(status.value);
                  setActiveTab('active');
                }}
                className={`p-3 md:p-4 rounded-xl border ${status.color} text-left transition-transform active:scale-[0.97] hover:shadow-sm cursor-pointer`}
                data-testid={`jobs-stat-${status.value}`}
              >
                <div className="flex items-center gap-2">
                  <status.icon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="font-semibold text-lg">{count}</span>
                </div>
                <p className="text-xs md:text-sm mt-1">{status.label}</p>
              </button>
            );
          })}
          {/* Show remaining statuses only on desktop */}
          {JOB_STATUSES.slice(3).map(status => {
            const count = jobs.filter(j => j.status === status.value).length;
            return (
              <button
                key={status.value}
                onClick={() => {
                  setActiveJobsFilter(status.value);
                  setActiveTab('active');
                }}
                className={`hidden md:block p-4 rounded-xl border ${status.color} text-left transition-transform active:scale-[0.97] hover:shadow-sm cursor-pointer`}
                data-testid={`jobs-stat-${status.value}`}
              >
                <div className="flex items-center gap-2">
                  <status.icon className="w-5 h-5" />
                  <span className="font-semibold text-lg">{count}</span>
                </div>
                <p className="text-sm mt-1">{status.label}</p>
              </button>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Active Jobs Tab Content */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-medium text-muted-foreground">Filter:</span>
            {['all', 'scheduled', 'in_progress', 'completed', 'on_hold', 'cancelled'].map(filter => (
              <Button
                key={filter}
                variant={activeJobsFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveJobsFilter(filter)}
                className="capitalize"
              >
                {filter === 'all' ? 'All' : filter.replace('_', ' ')}
                <span className="ml-1 text-xs opacity-70">
                  ({filter === 'all' 
                    ? actualJobs.length 
                    : actualJobs.filter(j => j.status === filter).length
                  })
                </span>
              </Button>
            ))}
          </div>

          {/* Active Jobs List */}
          <div className="space-y-3">
            {filteredActiveJobs.length === 0 ? (
              <div className="card-default text-center py-12">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No jobs found</p>
              </div>
            ) : (
              filteredActiveJobs.map(job => {
                const client = clients.find(c => c.id === job.client_id);
                const statusConfig = getStatusConfig(job.status);
                const linkedInvoice = job.invoice_id; // We'd need to fetch this
                
                return (
                  <div 
                    key={job.id} 
                    className="card-default hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div 
                        className="flex items-start gap-3 flex-1 cursor-pointer"
                        onClick={() => openJobDetail(job)}
                      >
                        <div 
                          className="w-3 h-full min-h-[60px] rounded-full flex-shrink-0"
                          style={{ backgroundColor: job.color || '#3b82f6' }}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg truncate">{job.title}</h3>
                          <p className="text-muted-foreground text-sm">{client?.name || 'Unknown Client'}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {job.start_date 
                                ? (job.start_date ? format(parseISO(job.start_date), 'MMM d, yyyy') : 'No date')
                                : 'Not scheduled'
                              }
                            </span>
                            {job.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span className="truncate max-w-[150px]">{job.address}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                        {/* Quick Status Dropdown */}
                        <Select
                          value={job.status}
                          onValueChange={(newStatus) => {
                            // Prevent event bubbling
                            handleUpdateJob(job.id, { status: newStatus });
                          }}
                        >
                          <SelectTrigger 
                            className={`w-[140px] h-8 text-xs font-semibold border-0 ${statusConfig.color}`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`job-status-select-${job.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent onClick={(e) => e.stopPropagation()}>
                            {JOB_STATUSES.map(status => {
                              const StatusIcon = status.icon;
                              return (
                                <SelectItem 
                                  key={status.value} 
                                  value={status.value}
                                  className="text-sm"
                                >
                                  <span className="flex items-center gap-2">
                                    <StatusIcon className="w-3.5 h-3.5" />
                                    {status.label}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {linkedInvoice && (
                          <span className="text-xs text-muted-foreground">{t('invoice_linked')}</span>
                        )}
                        <ArrowRight 
                          className="w-5 h-5 text-muted-foreground hidden sm:block cursor-pointer hover:text-primary transition-colors" 
                          onClick={() => openJobDetail(job)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Site Visits Tab Content */}
      {activeTab === 'appointments' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-bold">{t('site_visits_nav')}</h2>
              <p className="text-sm text-muted-foreground">
                Upcoming site visits for field assessments
              </p>
            </div>
            <Button 
              onClick={() => navigate('/site-visits?action=new')}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Site Visit
            </Button>
          </div>

          {siteVisits.filter(v => v.status === 'scheduled').length === 0 ? (
            <div className="card-default text-center py-12">
              <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">{t('no_site_visits_scheduled')}</p>
              <p className="text-sm text-muted-foreground">
                Schedule a site visit to do field assessments before creating estimates
              </p>
              <Button 
                onClick={() => navigate('/site-visits?action=new')}
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Site Visit
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {siteVisits
                .filter(v => v.status === 'scheduled')
                .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                .map(visit => {
                  const isPast = visit.scheduled_date ? isBefore(parseISO(visit.scheduled_date), new Date()) : false;
                  
                  return (
                    <div 
                      key={visit.id} 
                      className={`card-default hover:shadow-md transition-shadow cursor-pointer ${isPast ? 'opacity-60' : ''}`}
                      onClick={() => navigate(`/site-visits/${visit.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{visit.client_name}</h3>
                          <p className="text-sm text-muted-foreground">{visit.service_type || 'Site Assessment'}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {visit.scheduled_date ? format(parseISO(visit.scheduled_date), 'MMM d, yyyy') : 'No date'}
                            </span>
                            {visit.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {visit.scheduled_time}
                              </span>
                            )}
                            {visit.assigned_estimator_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {visit.assigned_estimator_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPast && (
                            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-1 rounded">Past Due</span>
                          )}
                          <ArrowRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Recurring Jobs Tab Content */}
      {activeTab === 'recurring' && (
        <div className="space-y-4" data-testid="recurring-jobs-tab">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-bold">Recurring Jobs</h2>
              <p className="text-sm text-muted-foreground">
                Templates that auto-generate jobs on a schedule (lawn care, cleaning, etc.)
              </p>
            </div>
            <Button onClick={() => setShowNewJobModal(true)} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>

          {(() => {
            const recurringTemplates = jobs.filter(j => j.is_recurring && !j.recurring_parent_id);
            if (recurringTemplates.length === 0) {
              return (
                <div className="card-default text-center py-10">
                  <Repeat className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="font-semibold text-gray-700">No recurring jobs yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Schedule a new job and toggle "Recurring job" to set up a weekly mow, biweekly clean, etc.
                  </p>
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {recurringTemplates.map((tmpl) => {
                  const upcoming = jobs
                    .filter(j => j.recurring_parent_id === tmpl.id && j.start_date && j.status !== 'cancelled')
                    .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
                  const nextOccurrence = upcoming.find(j => j.start_date >= format(new Date(), 'yyyy-MM-dd')) || upcoming[0];
                  const today = format(new Date(), 'yyyy-MM-dd');
                  const isStopped = tmpl.recurring_end_date && tmpl.recurring_end_date < today;

                  return (
                    <div
                      key={tmpl.id}
                      className="card-default"
                      data-testid={`recurring-template-${tmpl.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-base">{tmpl.title}</h3>
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
                              {tmpl.recurring_frequency || 'weekly'}
                            </span>
                            {isStopped && (
                              <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold uppercase tracking-wide">
                                Stopped
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {tmpl.client?.name || 'No customer'} · {upcoming.length} occurrence{upcoming.length === 1 ? '' : 's'} · {tmpl.address || 'No address'}
                          </p>
                          {nextOccurrence?.start_date && (
                            <p className="text-sm mt-2">
                              <span className="text-muted-foreground">Next: </span>
                              <span className="font-semibold">
                                {format(parseISO(nextOccurrence.start_date), 'EEE, MMM d')}
                                {nextOccurrence.start_time ? ` at ${nextOccurrence.start_time}` : ''}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/jobs/${tmpl.id}`)}
                            data-testid={`recurring-view-${tmpl.id}`}
                          >
                            View
                          </Button>
                          {!isStopped && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-700 hover:text-amber-800"
                              onClick={async () => {
                                if (!window.confirm('Generate the next 4 weeks of occurrences?')) return;
                                try {
                                  await apiHelpers.generateRecurringOccurrences(organization.id, tmpl.id, 4);
                                  toast.success('Generated next occurrences');
                                  loadJobs();
                                } catch (e) {
                                  console.error(e);
                                  toast.error('Could not generate occurrences');
                                }
                              }}
                              data-testid={`recurring-generate-${tmpl.id}`}
                            >
                              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Generate next
                            </Button>
                          )}
                          {!isStopped && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-700 hover:text-red-800"
                              onClick={async () => {
                                if (!window.confirm('Stop this recurring job? Future occurrences will be cancelled.')) return;
                                try {
                                  await apiHelpers.stopRecurringJob(organization.id, tmpl.id, user?.id, user?.name);
                                  toast.success('Recurring job stopped');
                                  loadJobs();
                                } catch (e) {
                                  console.error(e);
                                  toast.error('Could not stop recurring job');
                                }
                              }}
                              data-testid={`recurring-stop-${tmpl.id}`}
                            >
                              <StopCircle className="w-3.5 h-3.5 mr-1" /> Stop
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* New Job Modal */}
      <Dialog open={showNewJobModal} onOpenChange={setShowNewJobModal}>
        <DialogContent className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-2xl">Schedule New Job</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            {/* Client Selection */}
            <div>
              <Label className="text-sm sm:text-base">Client *</Label>
              <Select value={jobForm.client_id} onValueChange={handleClientSelect}>
                <SelectTrigger className="input-default mt-1 h-10 sm:h-11">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm sm:text-base">Job Title *</Label>
                <Input
                  value={jobForm.title}
                  onChange={e => setJobForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Patio Installation"
                  className="input-default mt-1 h-10 sm:h-11"
                />
              </div>
              <div>
                <Label className="text-sm sm:text-base">Job Type</Label>
                <Select 
                  value={jobForm.job_type} 
                  onValueChange={v => setJobForm(prev => ({ ...prev, job_type: v }))}
                >
                  <SelectTrigger className="input-default mt-1 h-10 sm:h-11">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(organization?.service_types_enabled || ['Concrete', 'Patio', 'Driveway', 'Sidewalk', 'Foundation']).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address */}
            <div>
              <Label className="text-sm sm:text-base">Job Address</Label>
              <Input
                value={jobForm.address}
                onChange={e => setJobForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Main St, City, ST"
                className="input-default mt-1 h-10 sm:h-11"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm sm:text-base">Start Date *</Label>
                <Input
                  type="date"
                  value={jobForm.start_date}
                  onChange={e => setJobForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="input-default mt-1 h-10 sm:h-11"
                />
              </div>
              <div>
                <Label className="text-sm sm:text-base">End Date</Label>
                <Input
                  type="date"
                  value={jobForm.end_date}
                  onChange={e => setJobForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="input-default mt-1 h-10 sm:h-11"
                />
              </div>
            </div>

            {/* Recurring Job Toggle - Only show if enabled for this org */}
            {organization?.feature_flags?.recurring_jobs && (
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm sm:text-base font-medium flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-green-600" />
                    Recurring Job
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Schedule this job to repeat automatically</p>
                </div>
                <Switch
                  checked={jobForm.is_recurring}
                  onCheckedChange={(checked) => setJobForm(prev => ({ ...prev, is_recurring: checked }))}
                  data-testid="recurring-job-toggle"
                />
              </div>
              
              {jobForm.is_recurring && (
                <div className="mt-4 space-y-3 pt-3 border-t border-green-200">
                  {/* Frequency */}
                  <div>
                    <Label className="text-sm">Repeat Frequency</Label>
                    <Select 
                      value={jobForm.recurring_frequency} 
                      onValueChange={v => setJobForm(prev => ({ ...prev, recurring_frequency: v }))}
                    >
                      <SelectTrigger className="input-default mt-1 h-10 bg-white" data-testid="recurring-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="custom">Custom Interval</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Custom interval */}
                  {jobForm.recurring_frequency === 'custom' && (
                    <div>
                      <Label className="text-sm">Repeat every X days</Label>
                      <Input
                        type="number"
                        min="1"
                        value={jobForm.recurring_interval_days || ''}
                        onChange={e => setJobForm(prev => ({ ...prev, recurring_interval_days: parseInt(e.target.value) || null }))}
                        placeholder="e.g., 10"
                        className="input-default mt-1 h-10 bg-white"
                      />
                    </div>
                  )}
                  
                  {/* End options */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">End Date (optional)</Label>
                      <Input
                        type="date"
                        value={jobForm.recurring_end_date}
                        onChange={e => setJobForm(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                        className="input-default mt-1 h-10 bg-white"
                        data-testid="recurring-end-date"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Max Occurrences</Label>
                      <Input
                        type="number"
                        min="1"
                        value={jobForm.recurring_occurrences || ''}
                        onChange={e => setJobForm(prev => ({ ...prev, recurring_occurrences: parseInt(e.target.value) || null }))}
                        placeholder="e.g., 12"
                        className="input-default mt-1 h-10 bg-white"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-green-700 bg-green-100 rounded-lg px-2 py-1.5">
                    The system will automatically schedule future occurrences on a rolling 4-week basis.
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Color */}
            <div>
              <Label className="text-sm sm:text-base">Calendar Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {JOB_COLORS.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setJobForm(prev => ({ ...prev, color: color.value }))}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 transition-transform ${
                      jobForm.color === color.value ? 'scale-110 border-gray-800' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm sm:text-base">Internal Notes</Label>
              <Textarea
                value={jobForm.internal_notes}
                onChange={e => setJobForm(prev => ({ ...prev, internal_notes: e.target.value }))}
                placeholder="Notes for your team..."
                className="mt-1 min-h-[60px] sm:min-h-[80px] text-sm"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button variant="outline" onClick={() => setShowNewJobModal(false)} className="flex-1 h-10 sm:h-12 text-sm">
                Cancel
              </Button>
              <Button onClick={handleCreateJob} className="flex-1 h-10 sm:h-12 btn-primary text-sm">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
                <span className="hidden sm:inline">Schedule</span> Job
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Detail Sheet */}
      <Sheet open={showJobDetail} onOpenChange={setShowJobDetail}>
        <SheetContent className="w-[95vw] max-w-lg overflow-y-auto p-4 sm:p-6">
          {selectedJob && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-lg sm:text-2xl">{selectedJob.title}</SheetTitle>
              </SheetHeader>
              
              <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                {/* Status */}
                <div>
                  <Label className="text-sm text-muted-foreground">Status</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {JOB_STATUSES.map(status => (
                      <button
                        key={status.value}
                        onClick={() => handleUpdateJob(selectedJob.id, { status: status.value })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                          selectedJob.status === status.value
                            ? status.color + ' ring-2 ring-offset-2'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <status.icon className="w-4 h-4 inline mr-1" />
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client */}
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{selectedJob.client?.name || 'Unknown Client'}</p>
                    {selectedJob.client?.phone && (
                      <p className="text-sm text-muted-foreground">{selectedJob.client.phone}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{selectedJob.address || 'No address'}</p>
                    <a 
                      href={`https://maps.google.com?q=${encodeURIComponent(selectedJob.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Open in Maps
                    </a>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-start gap-3">
                  <CalendarIcon className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {selectedJob.start_date && format(parseISO(selectedJob.start_date), 'EEEE, MMMM d, yyyy')}
                    </p>
                    {selectedJob.end_date && selectedJob.end_date !== selectedJob.start_date && (
                      <p className="text-sm text-muted-foreground">
                        to {format(parseISO(selectedJob.end_date), 'MMMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Job Type */}
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <p className="font-medium">{selectedJob.job_type}</p>
                </div>

                {/* Staff Assignments */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Assigned Staff
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddStaff(true)}
                      className="h-7 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {(!selectedJob.staff_assignments || selectedJob.staff_assignments.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-2">No staff assigned yet</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedJob.staff_assignments.map((assignment, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                          <div>
                            <p className="font-medium text-sm">{assignment.employee_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              assignment.duty === 'estimator' ? 'bg-blue-100 text-blue-700' :
                              assignment.duty === 'project_manager' ? 'bg-purple-100 text-purple-700' :
                              assignment.duty === 'crew_lead' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {DUTY_LABELS[assignment.duty] || assignment.custom_duty || assignment.duty}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveStaffFromJob(assignment.employee_id)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Add Staff Dialog */}
                <Dialog open={showAddStaff} onOpenChange={setShowAddStaff}>
                  <DialogContent className="w-[95vw] max-w-sm max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-base sm:text-lg">Assign Staff to Job</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <Label className="text-sm">Employee</Label>
                        <select
                          value={selectedStaffEmployee}
                          onChange={(e) => setSelectedStaffEmployee(e.target.value)}
                          className="input-default mt-1 w-full h-10 text-sm"
                        >
                          <option value="">Select employee...</option>
                          {employees
                            .filter(emp => !selectedJob?.staff_assignments?.some(a => a.employee_id === emp.id))
                            .map(emp => (
                              <option key={emp.id} value={emp.id}>
                                {emp.first_name} {emp.last_name} ({emp.role_type})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm">Duty / Role</Label>
                        <select
                          value={selectedStaffDuty}
                          onChange={(e) => setSelectedStaffDuty(e.target.value)}
                          className="input-default mt-1 w-full h-10 text-sm"
                        >
                          <option value="estimator">Estimator</option>
                          <option value="project_manager">Project Manager</option>
                          <option value="crew_lead">Crew Lead</option>
                          <option value="crew">Crew</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="flex gap-2 justify-end pt-2">
                        <Button variant="outline" onClick={() => setShowAddStaff(false)} className="h-9 sm:h-10 text-sm">Cancel</Button>
                        <Button onClick={handleAddStaffToJob} disabled={!selectedStaffEmployee} className="h-9 sm:h-10 text-sm">
                          Assign
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Internal Notes */}
                {selectedJob.internal_notes && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Internal Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedJob.internal_notes}</p>
                  </div>
                )}

                {/* Photos */}
                <div className="border-t pt-4">
                  <PhotoGallery
                    entityType="job"
                    entityId={selectedJob.id}
                    photos={selectedJob.photos || []}
                    onPhotosChange={(newPhotos) => {
                      const updatedJob = { ...selectedJob, photos: newPhotos };
                      setSelectedJob(updatedJob);
                      setJobs(jobs.map(j => j.id === selectedJob.id ? updatedJob : j));
                    }}
                  />
                </div>

                {/* Linked Documents */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Linked Documents</p>
                  {selectedJob.estimate_id && (
                    <a 
                      href={`/estimates/${selectedJob.estimate_id}`}
                      className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-blue-700 hover:bg-blue-100"
                    >
                      <FileText className="w-4 h-4" />
                      View Estimate
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                  {selectedJob.invoice_id && (
                    <a 
                      href={`/invoices/${selectedJob.invoice_id}`}
                      className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 hover:bg-green-100"
                    >
                      <Receipt className="w-4 h-4" />
                      View Invoice
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => handleDownloadPdf(selectedJob.id)}
                    data-testid="download-job-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download Job Sheet (PDF)
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-blue-600 hover:bg-blue-50"
                    onClick={() => handleAddToGoogleCalendar(selectedJob)}
                    data-testid="add-to-google-calendar-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Add to Google Calendar
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => handleExportIcal(selectedJob.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export to Calendar (.ics)
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-destructive hover:bg-destructive hover:text-white"
                    onClick={() => handleDeleteJob(selectedJob.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Job
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      
      {/* Job Completed - What's Next Prompt */}
      <JobCompletedPrompt
        open={showJobCompletedPrompt}
        onOpenChange={setShowJobCompletedPrompt}
        jobTitle={completedJobForPrompt?.title || ''}
        onCreateInvoice={() => {
          if (completedJobForPrompt) {
            navigate(`/jobs/${completedJobForPrompt.id}`);
          }
        }}
      />
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default JobsPage;
